import { Entity } from '@core/entities/entity'
import { IPermitTemplateVersion } from '@core/entities/permit-template-editor/IPermitTemplateVersion'
import { User } from '@core/entities/user'
import { plainToInstance, Type } from 'class-transformer'
import { localizedStrings } from '@core/strings'
import {
  CrossStageRule,
  CrossStageRuleReference,
  CurrentQuestionSetIdentifier,
  TemplateEditorPermitTemplateInitiatorRole,
  TemplateEditorPermitTemplateVersionStatus,
  TemplateEditorQuestionSet,
  TemplateEditorQuestionSetType,
} from '@core/entities/permit-template-editor/types'
import { Dictionary } from 'lodash'
import { ITemplateRule } from '@core/entities/template/TemplateRule'
import { TemplateForm } from '@core/entities/template/TemplateForm'
import { PermitTemplateApproval } from '@core/entities/permit-template-editor/PermitTemplateApproval'
import { PermitTemplateApprovalData } from '@core/entities/permit-template-editor/PermitTemplateApprovalData'
import { PermitTemplateFinalSignOff } from './PermitTemplateFinalSignOff'
import { PermitTemplateFinalSignOffData } from './PermitTemplateFinalSignOffData'
import { TemplateQuestion } from '@core/entities/template/TemplateQuestion'
import { TemplateWarning } from '@core/entities/template/TemplateWarnings'
import { IPermitTemplateApprovalRule } from './IPermitTemplateApprovalRule'
import { PermitTemplateSummary } from '@core/entities/permit/PermitTemplateSummary'
import { statuses } from '@core/theme/statuses'

export class TemplateEditorPermitTemplateVersion extends Entity<IPermitTemplateVersion> {
  id?: number

  @Type(() => User)
  createdBy: User

  createdOn?: string

  @Type(() => User)
  modifiedBy: User

  modifiedOn?: string

  version: number

  templateId: number

  status: TemplateEditorPermitTemplateVersionStatus

  initiatorRole: TemplateEditorPermitTemplateInitiatorRole

  approverConfirmationText: string

  maxDurationEnabled: boolean

  clonable?: boolean

  permitHolderTransferEnabled: boolean

  calculateDurationBasedOnDays: boolean

  allowExtensionBeyondMaxDuration: boolean

  @Type(() => PermitTemplateApproval)
  approvals: PermitTemplateApproval

  @Type(() => PermitTemplateFinalSignOff)
  finalSignOffs: PermitTemplateFinalSignOff

  permitHolderSelfApprove: boolean

  permitHolderSelfSignoff: boolean

  requiresFinalSignoff: boolean

  @Type(() => TemplateForm)
  permitHolderQuestions: TemplateForm

  @Type(() => TemplateForm)
  postPermitChecksQuestions: TemplateForm

  @Type(() => TemplateForm)
  authorisedPersonQuestions: TemplateForm

  skipPostPermitChecks: boolean

  requesterConfirmationText: string

  requesterConfirmationCountdown: { time: number; name: string }

  maxDurationSeconds?: number

  maxDurationDays?: number

  maxDurationIncludingExtensionsDays?: number

  requiresApproval?: boolean

  requiresBriefingVerification?: boolean

  requiresBriefingSignature?: boolean

  requiresBriefingAcceptance?: boolean

  requireGeolocationForBriefings?: boolean

  requireGeolocationForPermit?: boolean

  enableCheckIn?: boolean

  requiresTransferApproval?: boolean

  allowSuspension: boolean

  allowShorten: boolean

  allowRequestExtension: boolean

  allowApprovalDrafts: boolean

  @Type(() => TemplateWarning)
  globalWarnings: TemplateWarning[]

  @Type(() => TemplateWarning)
  questionWarnings: TemplateWarning[]

  @Type(() => PermitTemplateSummary)
  allowedChildTemplates?: PermitTemplateSummary[]

  public isActive(): boolean {
    return this.status === 'ACTIVE'
  }

  public isReadOnly(): boolean {
    return this.status !== 'DRAFT'
  }

  public static new(payload: unknown): TemplateEditorPermitTemplateVersion {
    const entity = plainToInstance(TemplateEditorPermitTemplateVersion, payload)

    return entity
  }

  public static statusColor(status: TemplateEditorPermitTemplateVersionStatus) {
    switch (status) {
      case 'ACTIVE':
        return statuses.active.color
      case 'ARCHIVED':
        return statuses.archived.color
      case 'DISCARDED':
        return statuses.discarded.color
      case 'DRAFT':
        return statuses.draft.color
      default:
        return 'default'
    }
  }

  public static statusText(status: TemplateEditorPermitTemplateVersionStatus) {
    switch (status) {
      case 'ACTIVE':
        return statuses.active.label
      case 'ARCHIVED':
        return statuses.archived.label
      case 'DISCARDED':
        return statuses.discarded.label
      case 'DRAFT':
        return statuses.draft.label
      default:
        return status
    }
  }

  public getQuestionInfo(questionId: string) {
    const getInfo = (templateQuestions: TemplateForm, sourceText) => {
      const groupIndex = templateQuestions.questions.findIndex(
        (questionGroup) => questionGroup.data.find((q) => q.id === questionId),
      )
      const questionIndex = templateQuestions.questions[
        groupIndex
      ].data.findIndex((q) => q.id === questionId)
      return {
        source: sourceText,
        groupIndex,
        questionIndex,
        body: TemplateQuestion.getBodyFormattedText(
          templateQuestions.questions[groupIndex].data[questionIndex].body,
        ),
      }
    }

    if (
      this.permitHolderQuestions.questions.find((questionGroup) =>
        questionGroup.data.find((q) => q.id === questionId),
      )
    ) {
      return getInfo(this.permitHolderQuestions, localizedStrings.permitHolder)
    }
    if (
      this.postPermitChecksQuestions.questions.find((questionGroup) =>
        questionGroup.data.find((q) => q.id === questionId),
      )
    ) {
      return getInfo(
        this.postPermitChecksQuestions,
        localizedStrings.postPermitChecks,
      )
    }
    if (
      this.authorisedPersonQuestions.questions.find((questionGroup) =>
        questionGroup.data.find((q) => q.id === questionId),
      )
    ) {
      return getInfo(
        this.authorisedPersonQuestions,
        localizedStrings.authorisedPerson,
      )
    }
    return null
  }

  public hasCountdownQuestion() {
    return (
      this.permitHolderQuestions?.questions.some((questionGroup) =>
        questionGroup.data.some(
          (q) => q.questionType === 'CONFIGURE_POST_PERMIT_CHECKS_COUNTDOWN',
        ),
      ) ||
      this.authorisedPersonQuestions?.questions.some((questionGroup) =>
        questionGroup.data.some(
          (q) => q.questionType === 'CONFIGURE_POST_PERMIT_CHECKS_COUNTDOWN',
        ),
      ) ||
      this.approvals?.data.some((approval) =>
        approval.approvalQuestions?.questions.some((questionGroup) =>
          questionGroup.data.some(
            (q) => q.questionType === 'CONFIGURE_POST_PERMIT_CHECKS_COUNTDOWN',
          ),
        ),
      )
    )
  }

  public getQuestionSets(approvalId?: string): TemplateEditorQuestionSet[] {
    const questionSets = []
    const approvals = this.approvals?.data ?? []

    if (!approvalId) {
      if (this.initiatorRole === 'AUTHORISED_PERSON') {
        questionSets.push({
          name: localizedStrings.authorisedPerson,
          type: 'AUTH_PERSON_QUESTIONS',
          questions: this.authorisedPersonQuestions.questions,
          rules: this.approvals?.rules,
        })
      }

      questionSets.push({
        name: localizedStrings.permitHolder,
        type: 'PERMIT_HOLDER_QUESTIONS',
        questions: this.permitHolderQuestions.questions,
        rules: this.approvals?.rules,
      })

      questionSets.push(
        ...approvals.map(
          TemplateEditorPermitTemplateVersion.createApprovalQuestionSet,
        ),
      )

      return questionSets
    }

    const currentApprovalIndex = approvals.findIndex((a) => a.id === approvalId)
    const previousApprovals =
      currentApprovalIndex > -1 ? approvals.slice(0, currentApprovalIndex) : []

    // Add auth person questions if initiator is authorized person
    if (this.initiatorRole === 'AUTHORISED_PERSON') {
      questionSets.push({
        name: localizedStrings.authorisedPerson,
        type: 'AUTH_PERSON_QUESTIONS',
        questions: this.authorisedPersonQuestions.questions,
        rules: this.approvals?.rules,
      })
    }

    // Add BEFORE_PERMIT_HOLDER approvals
    const beforePermitHolderApprovals = previousApprovals.filter(
      TemplateEditorPermitTemplateVersion.isBeforePermitHolder,
    )
    beforePermitHolderApprovals.forEach((approval) =>
      questionSets.push(
        TemplateEditorPermitTemplateVersion.createApprovalQuestionSet(approval),
      ),
    )

    // Add permit holder questions only if all BEFORE_PERMIT_HOLDER approvals have been processed
    const allBeforePermitHolderApprovals = approvals.filter(
      TemplateEditorPermitTemplateVersion.isBeforePermitHolder,
    )
    if (
      allBeforePermitHolderApprovals.length ===
      beforePermitHolderApprovals.length
    ) {
      questionSets.push({
        name: localizedStrings.permitHolder,
        type: 'PERMIT_HOLDER_QUESTIONS',
        questions: this.permitHolderQuestions.questions,
        rules: this.approvals?.rules,
      })
    }

    // Add AFTER_PERMIT_HOLDER approvals
    const afterPermitHolderApprovals = previousApprovals.filter(
      TemplateEditorPermitTemplateVersion.isAfterPermitHolder,
    )
    afterPermitHolderApprovals.forEach((approval) =>
      questionSets.push(
        TemplateEditorPermitTemplateVersion.createApprovalQuestionSet(approval),
      ),
    )

    return questionSets
  }

  public getSelectedQuestionSet(
    questionSet?: IPermitTemplateApprovalRule['questionSet'],
    approvalLabel?: string,
  ) {
    const isMatchingQuestionSet = (
      questionSet: TemplateEditorQuestionSet,
      targetType: TemplateEditorQuestionSetType,
      approvalLabel: string,
    ) => {
      // Non-approval question sets that match the target type
      if (
        questionSet.type === targetType &&
        questionSet.type !== 'APPROVAL_QUESTIONS'
      ) {
        return true
      }

      // Approval question sets that match the approval label
      if (
        questionSet.type === 'APPROVAL_QUESTIONS' &&
        questionSet.name === approvalLabel
      ) {
        return true
      }

      // Add additional conditions here as needed
      return false
    }

    const targetType = questionSet?.type

    const templateForm = TemplateForm.new(
      this.getQuestionSets()?.find((q) =>
        isMatchingQuestionSet(q, targetType, approvalLabel),
      ),
    )

    return templateForm
  }

  public getApprovalQuestionSets() {
    return (
      this.approvals?.data.map(
        TemplateEditorPermitTemplateVersion.createApprovalQuestionSet,
      ) ?? []
    )
  }

  /**
   * Returns ALL question sets in the template with their proper rules.
   * Unlike getAvailableQuestionSetsForStage (which returns only previous stages),
   * this returns every stage for the purpose of finding cross-stage rule references.
   *
   * @returns Array of all question sets in the template
   */
  public getAllQuestionSets(): TemplateEditorQuestionSet[] {
    const questionSets = []
    const approvals = this.approvals?.data ?? []
    const finalSignOffs = this.finalSignOffs?.data ?? []

    // 1. AUTH_PERSON_QUESTIONS (if initiator is authorised person)
    if (this.initiatorRole === 'AUTHORISED_PERSON') {
      questionSets.push({
        name: localizedStrings.authorisedPerson,
        type: 'AUTH_PERSON_QUESTIONS',
        questions: this.authorisedPersonQuestions?.questions,
        rules: this.authorisedPersonQuestions?.rules,
      })
    }

    // 2. BEFORE_PERMIT_HOLDER approvals
    approvals
      .filter(TemplateEditorPermitTemplateVersion.isBeforePermitHolder)
      .forEach((approval) => {
        questionSets.push(
          TemplateEditorPermitTemplateVersion.createApprovalQuestionSet(
            approval,
          ),
        )
      })

    // 3. PERMIT_HOLDER_QUESTIONS
    questionSets.push({
      name: localizedStrings.permitHolder,
      type: 'PERMIT_HOLDER_QUESTIONS',
      questions: this.permitHolderQuestions?.questions,
      rules: this.permitHolderQuestions?.rules,
    })

    // 4. AFTER_PERMIT_HOLDER approvals
    approvals
      .filter(TemplateEditorPermitTemplateVersion.isAfterPermitHolder)
      .forEach((approval) => {
        questionSets.push(
          TemplateEditorPermitTemplateVersion.createApprovalQuestionSet(
            approval,
          ),
        )
      })

    // 5. POST_PERMIT_CHECKS_QUESTIONS
    questionSets.push({
      name: localizedStrings.postPermitChecks,
      type: 'POST_PERMIT_CHECKS_QUESTIONS',
      questions: this.postPermitChecksQuestions?.questions,
      rules: this.postPermitChecksQuestions?.rules,
    })

    // 6. FINAL_SIGN_OFF_QUESTIONS
    finalSignOffs.forEach((signOff) => {
      questionSets.push(
        TemplateEditorPermitTemplateVersion.createFinalSignOffQuestionSet(
          signOff,
        ),
      )
    })

    return questionSets
  }

  /**
   * Returns question sets available for conditional logic based on the current stage.
   * Includes all question sets that come BEFORE the current stage, PLUS the current stage itself.
   * This allows both cross-stage and same-stage conditional logic.
   *
   * Permit workflow order:
   * 1. AUTH_PERSON_QUESTIONS (if initiator is authorised person)
   * 2. APPROVAL_QUESTIONS (BEFORE_PERMIT_HOLDER) - each in sequence
   * 3. PERMIT_HOLDER_QUESTIONS
   * 4. APPROVAL_QUESTIONS (AFTER_PERMIT_HOLDER) - each in sequence
   * 5. POST_PERMIT_CHECKS_QUESTIONS
   * 6. FINAL_SIGN_OFF_QUESTIONS - each in sequence
   *
   * Example: For the 2nd BEFORE_PERMIT_HOLDER approval, returns:
   * [AUTH_PERSON (if applicable), 1st BEFORE_PH approval, 2nd BEFORE_PH approval (current)]
   *
   * @param currentQuestionSet - The stage we're currently editing
   * @returns Array of question sets from start of flow up to and including current stage
   */
  public getAvailableQuestionSetsForStage(
    currentQuestionSet: CurrentQuestionSetIdentifier,
  ): TemplateEditorQuestionSet[] {
    const questionSets = []
    const approvals = this.approvals?.data ?? []
    const finalSignOffs = this.finalSignOffs?.data ?? []

    // Helper to check if we've reached the current stage (returns true = stop here)
    const isCurrentStage = (
      type: TemplateEditorQuestionSetType,
      stageId?: string,
    ): boolean => {
      if (type !== currentQuestionSet.questionSetType) return false
      if (
        type === 'APPROVAL_QUESTIONS' ||
        type === 'FINAL_SIGN_OFF_QUESTIONS'
      ) {
        return stageId === currentQuestionSet.id
      }
      return true
    }

    // 1. AUTH_PERSON_QUESTIONS (first in flow, if applicable)
    if (this.initiatorRole === 'AUTHORISED_PERSON') {
      questionSets.push({
        name: localizedStrings.authorisedPerson,
        type: 'AUTH_PERSON_QUESTIONS',
        questions: this.authorisedPersonQuestions?.questions,
        rules: this.authorisedPersonQuestions?.rules,
      })
      if (isCurrentStage('AUTH_PERSON_QUESTIONS')) {
        return questionSets
      }
    }

    // 2. BEFORE_PERMIT_HOLDER approvals (in order)
    const beforePHApprovals = approvals.filter(
      TemplateEditorPermitTemplateVersion.isBeforePermitHolder,
    )
    for (const approval of beforePHApprovals) {
      questionSets.push(
        TemplateEditorPermitTemplateVersion.createApprovalQuestionSet(approval),
      )
      if (isCurrentStage('APPROVAL_QUESTIONS', approval.id)) {
        return questionSets
      }
    }

    // 3. PERMIT_HOLDER_QUESTIONS
    questionSets.push({
      name: localizedStrings.permitHolder,
      type: 'PERMIT_HOLDER_QUESTIONS',
      questions: this.permitHolderQuestions?.questions,
      rules: this.permitHolderQuestions?.rules,
    })
    if (isCurrentStage('PERMIT_HOLDER_QUESTIONS')) {
      return questionSets
    }

    // 4. AFTER_PERMIT_HOLDER approvals (in order)
    const afterPHApprovals = approvals.filter(
      TemplateEditorPermitTemplateVersion.isAfterPermitHolder,
    )
    for (const approval of afterPHApprovals) {
      questionSets.push(
        TemplateEditorPermitTemplateVersion.createApprovalQuestionSet(approval),
      )
      if (isCurrentStage('APPROVAL_QUESTIONS', approval.id)) {
        return questionSets
      }
    }

    // 5. POST_PERMIT_CHECKS_QUESTIONS
    questionSets.push({
      name: localizedStrings.postPermitChecks,
      type: 'POST_PERMIT_CHECKS_QUESTIONS',
      questions: this.postPermitChecksQuestions?.questions,
      rules: this.postPermitChecksQuestions?.rules,
    })
    if (isCurrentStage('POST_PERMIT_CHECKS_QUESTIONS')) {
      return questionSets
    }

    // 6. FINAL_SIGN_OFF_QUESTIONS (each in sequence)
    for (const signOff of finalSignOffs) {
      questionSets.push(
        TemplateEditorPermitTemplateVersion.createFinalSignOffQuestionSet(
          signOff,
        ),
      )
      if (isCurrentStage('FINAL_SIGN_OFF_QUESTIONS', signOff.id)) {
        return questionSets
      }
    }

    // Fallback: return all if current stage not found
    return questionSets
  }

  private static isBeforePermitHolder(a: PermitTemplateApprovalData): boolean {
    return a.approvalStage === 'BEFORE_PERMIT_HOLDER'
  }

  private static isAfterPermitHolder(a: PermitTemplateApprovalData): boolean {
    return a.approvalStage === 'AFTER_PERMIT_HOLDER'
  }

  private static createApprovalQuestionSet(
    approval: PermitTemplateApprovalData,
  ) {
    return {
      name: approval?.label ?? '',
      type: 'APPROVAL_QUESTIONS' as const,
      id: approval.id,
      approvalId: approval.id,
      questions: approval.approvalQuestions?.questions,
      rules: approval.approvalQuestions?.rules,
    }
  }

  private static createFinalSignOffQuestionSet(
    signOff: PermitTemplateFinalSignOffData,
  ) {
    return {
      name: signOff?.label ?? '',
      type: 'FINAL_SIGN_OFF_QUESTIONS' as const,
      id: signOff.id,
      finalSignOffId: signOff.id,
      questions: signOff.finalSignOffQuestions?.questions,
      rules: signOff.finalSignOffQuestions?.rules,
    }
  }

  /**
   * Helper to check if two question sets refer to the same stage.
   */
  private static isSameQuestionSet(
    questionSet: TemplateEditorQuestionSet,
    currentQuestionSet: CurrentQuestionSetIdentifier,
  ): boolean {
    if (questionSet.type !== currentQuestionSet.questionSetType) return false
    if (
      questionSet.type === 'APPROVAL_QUESTIONS' ||
      questionSet.type === 'FINAL_SIGN_OFF_QUESTIONS'
    ) {
      return questionSet.id === currentQuestionSet.id
    }
    return true
  }

  /**
   * Find a question set from a list that matches the given identifier.
   */
  public static findQuestionSet(
    questionSets: TemplateEditorQuestionSet[],
    identifier: CurrentQuestionSetIdentifier | null | undefined,
  ): TemplateEditorQuestionSet | null {
    if (!identifier) return null
    return (
      questionSets.find((qs) => this.isSameQuestionSet(qs, identifier)) || null
    )
  }

  /**
   * Find a question or question group by ID across multiple question sets.
   * Returns the question, its position indices, and the containing question set info.
   * Also matches question group container IDs (not just individual question IDs in data[]).
   */
  public static findQuestionInQuestionSets(
    questionSets: TemplateEditorQuestionSet[],
    questionId: string | number | undefined,
  ): {
    question: TemplateQuestion
    questionSetName: string
    questionSetType: TemplateEditorQuestionSetType
    questionSetId?: string
    groupIndex: number
    questionIndex: number
    isQuestionGroup?: boolean
    questionGroupTitle?: string
  } | null {
    if (!questionId) return null
    for (const qs of questionSets) {
      if (!qs.questions) continue
      for (let gIdx = 0; gIdx < qs.questions.length; gIdx++) {
        const qGroup = qs.questions[gIdx]
        const qIdx = qGroup.data?.findIndex((q) => q.id === questionId) ?? -1
        if (qIdx !== -1) {
          return {
            question: qGroup.data[qIdx],
            questionSetName: qs.name,
            questionSetType: qs.type,
            questionSetId: qs.id,
            groupIndex: gIdx,
            questionIndex: qIdx,
          }
        }
        if (qGroup.id === questionId && !qGroup.singular) {
          return {
            question: qGroup.data?.[0],
            questionSetName: qs.name,
            questionSetType: qs.type,
            questionSetId: qs.id,
            groupIndex: gIdx,
            questionIndex: 0,
            isQuestionGroup: true,
            questionGroupTitle: qGroup.title,
          }
        }
      }
    }
    return null
  }

  /**
   * Helper to check if a condition's questionSet matches the current stage.
   */
  public static conditionMatchesCurrentStage(
    conditionQuestionSet: CurrentQuestionSetIdentifier | null | undefined,
    currentQuestionSet: CurrentQuestionSetIdentifier,
  ): boolean {
    if (!conditionQuestionSet) return false
    if (
      conditionQuestionSet.questionSetType !==
      currentQuestionSet.questionSetType
    ) {
      return false
    }
    if (
      conditionQuestionSet.questionSetType === 'APPROVAL_QUESTIONS' ||
      conditionQuestionSet.questionSetType === 'FINAL_SIGN_OFF_QUESTIONS'
    ) {
      return conditionQuestionSet.id === currentQuestionSet.id
    }
    return true
  }

  /**
   * Get cross-stage rules that reference questions from the specified stage.
   * Used to show chip indicators on questions that control visibility in other stages.
   *
   * @param availableQuestionSets - All question sets available (from context)
   * @param currentQuestionSet - The stage we're currently viewing
   * @returns Dictionary mapping questionId to array of cross-stage rule references
   */
  public static getCrossStageRulesByQuestionId(
    availableQuestionSets: TemplateEditorQuestionSet[],
    currentQuestionSet: CurrentQuestionSetIdentifier | undefined,
  ): Dictionary<CrossStageRuleReference[]> {
    if (!currentQuestionSet) return {}

    const result: Dictionary<CrossStageRuleReference[]> = {}

    for (const questionSet of availableQuestionSets) {
      // Skip if this IS the current question set
      if (this.isSameQuestionSet(questionSet, currentQuestionSet)) continue

      // Find rules that reference questions from the current stage
      questionSet.rules?.forEach((rule) => {
        rule.conditions?.forEach((condition) => {
          const cqs = condition.conditionQuestionSet
          if (
            this.conditionMatchesCurrentStage(cqs, currentQuestionSet) &&
            condition.questionId
          ) {
            // Group by questionId - add or increment count
            if (!result[condition.questionId]) {
              result[condition.questionId] = []
            }
            const existing = result[condition.questionId].find(
              (r) =>
                r.stageType === questionSet.type &&
                r.stageName === questionSet.name,
            )
            if (existing) {
              existing.ruleCount++
            } else {
              result[condition.questionId].push({
                stageName: questionSet.name,
                stageType: questionSet.type,
                ruleCount: 1,
              })
            }
          }
        })
      })
    }

    return result
  }

  /**
   * Get rules from other stages where the specified question's answers control visibility.
   * Used in Tab 2 "Rules set by this question" in the conditional logic modal.
   *
   * @param availableQuestionSets - All question sets available (from context)
   * @param currentQuestionSet - The stage we're currently viewing
   * @param questionId - The question ID to find rules for
   * @returns Array of cross-stage rules controlled by this question
   */
  public static getCrossStageRulesControlledByQuestion(
    availableQuestionSets: TemplateEditorQuestionSet[],
    currentQuestionSet: CurrentQuestionSetIdentifier | undefined,
    questionId: string,
  ): CrossStageRule[] {
    if (!currentQuestionSet || !questionId) return []

    const result: CrossStageRule[] = []

    for (const questionSet of availableQuestionSets) {
      // Skip if this IS the current question set
      if (this.isSameQuestionSet(questionSet, currentQuestionSet)) continue

      // Find rules where condition references this question from the current stage
      questionSet.rules?.forEach((rule) => {
        const matchingCondition = rule.conditions?.some(
          (condition) =>
            condition.questionId === questionId &&
            this.conditionMatchesCurrentStage(
              condition.conditionQuestionSet,
              currentQuestionSet,
            ),
        )
        if (matchingCondition) {
          result.push({
            rule,
            stageName: questionSet.name,
            stageType: questionSet.type,
          })
        }
      })
    }

    return result
  }

  /**
   * Get cross-stage visibility references for a specific question.
   * Returns which stages control this question's visibility via cross-stage rules.
   * Used to show "Visibility controlled by N rule(s) in [Stage Name]" chips
   * on the dependent question.
   *
   * This is the inverse of getCrossStageRulesByQuestionId:
   * - getCrossStageRulesByQuestionId: "which questions here control OTHER stages"
   * - getCrossStageVisibilityReferences: "which OTHER stages control this question here"
   *
   * @param rules - The current stage's rules (from templateForm)
   * @param questionId - The question to check
   * @param currentQuestionSet - The stage we're currently viewing
   * @param availableQuestionSets - Question sets available for resolving stage names
   * @returns Array of { stageName, stageType, ruleCount } for each cross-stage source
   */
  public static getCrossStageVisibilityReferences(
    rules: ITemplateRule[] | undefined,
    questionId: string,
    currentQuestionSet: CurrentQuestionSetIdentifier | undefined,
    availableQuestionSets?: TemplateEditorQuestionSet[],
  ): CrossStageRuleReference[] {
    if (!currentQuestionSet || !questionId || !rules) return []

    const stageMap = new Map<
      string,
      { stageType: TemplateEditorQuestionSetType; count: number }
    >()

    rules.forEach((rule) => {
      const isTargetQuestion = rule.shownQuestions?.some(
        (sq) => sq.questionId === questionId,
      )
      if (!isTargetQuestion) return

      rule.conditions?.forEach((condition) => {
        const cqs = condition.conditionQuestionSet
        if (!cqs) return

        if (!this.conditionMatchesCurrentStage(cqs, currentQuestionSet)) {
          const sourceStage = this.findQuestionSet(
            availableQuestionSets ?? [],
            cqs,
          )
          const name = sourceStage?.name || cqs.questionSetType
          const existing = stageMap.get(name)
          if (existing) {
            existing.count++
          } else {
            stageMap.set(name, { stageType: cqs.questionSetType, count: 1 })
          }
        }
      })
    })

    const refs: CrossStageRuleReference[] = []
    stageMap.forEach(({ stageType, count }, name) => {
      refs.push({ stageName: name, stageType, ruleCount: count })
    })
    return refs
  }
}
